import axios, {AxiosError, AxiosResponse} from 'axios';
import queryString from 'query-string';
import {Role, UserObject} from 'stores/UserStore';
import {PaymentObject, StudentObject} from './student/types';
import {
  ID,
  Slug,
  PriceObject,
  PreviewObject,
  RangeDateObject,
  PromocodeObject,
  ErrorObject,
  ToggleProperty,
  Tag,
  Folder,
} from 'types';
import {toast} from 'react-toastify';

export enum CourseStatus {
  Public = 'public',
  Private = 'private',
  Draft = 'draft',
  Ban = 'ban',
}

export type CourseSettings<T extends Partial<CourseObject>> = Pick<T,
  | 'chatLink'
  | 'fixedDate'
  | 'postAccess'
  | 'sale'
  | 'postOffer'
  | 'postMessage'
  | 'promocodes'
  | 'resale'
  | 'finalLink'
  | 'salesDates'
  | 'audience'
  | 'benefit'
  | 'teachers'
  | 'videoMessage'
  | 'faq'>;

export enum ProgramType {
  Growplace = 'growplace',
  Fitplace = 'fitplace',
}

export enum ChapterContentType {
  Lesson = 'lesson',
  Task = 'task',
  Webinar = 'webinar',
  Training = 'gym',
  Food = 'eat',
}

export interface ChapterContentObject {
  id: ID;
  instanceId: ID;
  type: ChapterContentType;
}

export interface ChapterObject {
  id: ID;
  title?: string;
  content?: ChapterContentObject[];
}

export interface GrowplaceProgram {
  chapters: ChapterObject[];
}

export interface FitplaceProgram {
  eat: ID[];
  gym: ID[];
  read: ID[];
}

export type CourseEmbedded =
  Pick<CourseObject,
    | 'id'
    | 'preview'> & {
  discount: number;
};

export interface ProgramObject {
  type: ProgramType;
  content: GrowplaceProgram | FitplaceProgram;
}

export interface FaqObject {
  question: string;
  answer: string;
}

export interface AudienceObject {
  preview?: PreviewObject;
  title: string;
  description?: string;
}

export interface BenefitObject {
  description: string;
}

export interface TeacherObject {
  preview?: PreviewObject;
  text: string;
  name: string;
  role: string;
}

export interface CourseObject {
  id: ID;
  userID?: ID;
  createdAt?: Date;
  counters?: {
    lesson: number,
    read: number,
    webinar: number
  };
  title: string;
  folders?: Folder[];
  description: string;
  slug: Slug;
  price: Partial<PriceObject>;
  status: CourseStatus;
  students?: ID[];
  preview?: PreviewObject;
  tags?: string[];
  chatLink?: string;
  fixedDate?: ToggleProperty<{
    dates: RangeDateObject;
  }>;
  postAccess?: ToggleProperty<{
    days: number;
  }>;
  salesDates?: RangeDateObject;
  sale?: ToggleProperty<{
    dates: RangeDateObject;
    price: number;
    limits: number;
  }>;
  postOffer?: ToggleProperty<{
    description: string;
    price: CourseObject['price']['amount'];
    limit: number;
    courses: {
      id: CourseObject['id'];
      discount: number;
    }[];
  }>;
  freeCourses?: ToggleProperty <{
    list: CourseObject['id'][]
  }>;
  postMessage?: ToggleProperty<{
    text: string;
  }>;
  promocodes?: ToggleProperty<{
    list: PromocodeObject[];
  }>;
  resale?: ToggleProperty<{
    discount: number;
  }>;
  finalLink?: ToggleProperty<{
    link: string;
  }>;
  programs: ProgramObject;
  video?: ToggleProperty<{
    preview: PreviewObject;
  }>;
  audience?: ToggleProperty<{
    title: string;
    list: AudienceObject[];
  }>;
  benefit?: ToggleProperty<{
    title: string;
    list: BenefitObject[];
  }>;
  authorMessage?: ToggleProperty<{
    teacher: TeacherObject;
  }>;
  teachers?: ToggleProperty<{
    list: TeacherObject[];
  }>;
  videoMessage?: ToggleProperty<{
    preview: PreviewObject;
  }>;
  faq?: ToggleProperty<{
    title: string;
    list: FaqObject[];
  }>;
}

export interface CoursePromoObject extends Omit<CourseObject, 'freeCourses'> {
  freeCourses?: ToggleProperty <{
    list: CourseObject[]
  }>;
}

export type GetCoursesParams =
  Partial<Pick<CourseObject,
    'userID' |
    'status' |
    'title'
    >> & {
  skip?: string;
  my?: string;
  tag?: string;
  folder?: string;
  limit?: string;
};

/**
 * Get courses
 * @param {GetCoursesParams} params
 * @return {Promise<CourseObject[] | ErrorObject | undefined>}
 */
export async function getCourses(params?: GetCoursesParams) {
  return axios({
    method: 'GET',
    url: queryString.stringifyUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/courses`,
      query: params && {
        ...params,
      },
    }),
  }).then((response: AxiosResponse<CourseObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get courses by IDs array
 * @param {ID[]} params?
 * @return {Promise<CourseObject[] | ErrorObject | undefined>}
 */
export async function getCoursesByIDsArray(params?: ID[]) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/get-many`,
    data: JSON.stringify({ids: params}),
  }).then((response: AxiosResponse<CourseObject[]>) => {
    return response.data;
  }).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get course by id
 * @param {CourseObject<id>} id
 * @return {Promise<CourseObject | ErrorObject>}
 */
export async function getCourseByID(id: CourseObject['id']) {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/${id}`,
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Create the course
 * @param {Omit<CourseObject, 'id'>} course
 * @return {Promise<CourseObject | ErrorObject>}
 */
export async function createCourse(course: Omit<CourseObject, 'id'>) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses`,
    data: JSON.stringify(course),
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Edit the course
 * @param {CourseObject} course
 * @return {Promise<CourseObject | ErrorObject>}
 */
export async function editCourse(course: CourseObject) {
  return axios({
    method: 'PATCH',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses`,
    data: JSON.stringify(course),
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Delete the course
 * @param {CourseObject<id>} id
 * @return {Promise<CourseObject | ErrorObject>}
 */
export async function deleteCourse(id: CourseObject['id']) {
  return axios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/${id}`,
    data: JSON.stringify({
      id,
    }),
  }).then((response: AxiosResponse<CourseObject>) => {
    toast.success('Курс удален!');
    return response.data;
  }).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Copy the course
 * @param {CourseObject} course
 * @return {Promise<CourseObject | ErrorObject>}
 */
export async function copyCourse(course: CourseObject) {
  return axios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/clone`,
    data: JSON.stringify(course),
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Helper answering the question is the current course free.
 * @param {Partial<CourseObject>} course
 * @return {boolean}
 */
export function isFree(course: Partial<CourseObject>): boolean {
  return !Boolean(course.price?.amount);
}

/**
 * Is the course published.
 * @param {CourseStatus} status
 * @return {boolean}
 */
export function isPublished(status: CourseStatus): boolean {
  return status === CourseStatus.Public;
}

/**
 * Get status label.
 * @param {CourseStatus} status
 * @return {string}
 */
export function getStatusLabel(status: CourseStatus): string {
  return isPublished(status) ? 'Опубликован' : 'Не опубликован';
}

/**
 * Invite student in the course.
 * @param {CourseObject<id>} courseID
 * @param {UserObject<id>} studentID
 * @return {Promise<CourseObject | ErrorObject | undefined>}
 */
export async function inviteStudent(
    courseID: CourseObject['id'],
    studentID: UserObject['id'],
) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/students`,
    data: JSON.stringify({
      courseID,
      studentID,
    }),
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get course by id
 * @return {Promise<CourseObject | ErrorObject>}
 * @param {UserObject<username>} teacherName
 * @param {CourseObject<slug>} slug
 */
export async function getCoursePromo(
    teacherName: UserObject['username'],
    slug: CourseObject['slug'],
) {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/courses/promo/${teacherName}/${slug}`,
  }).then((response: AxiosResponse<CourseObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Invite students in the course.
 * @param {CourseObject<id>} courseID
 * @param {UserObject<id>[]} studentIDs
 * @return {Promise<CourseObject | ErrorObject | undefined>}
 */
export async function inviteStudents(
    courseID: CourseObject['id'],
    studentIDs: UserObject['id'][],
) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/students/bulk`,
    data: JSON.stringify({
      courseID,
      studentIDs,
    }),
  }).then((response: AxiosResponse<PaymentObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get students of the course.
 * @param {CourseObject<id>} courseID
 * @return {Promise<CourseObject | ErrorObject | undefined>}
 */
export async function getStudents(
    courseID: CourseObject['id'],
) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/students/select`,
    data: JSON.stringify({
      courseID: courseID,
    }),
  }).then((response: AxiosResponse<StudentObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

export interface StudentItemObject {
  courseTitle: string;
  userID: ID;
  status: string;
  name: string;
  email: string;
  avatar: PreviewObject;
  role: Role;
}

/**
 * Get students list.
 * @param {number} skip
 * @param {number} limit
 * @return {Promise<CourseObject | ErrorObject | undefined>}
 */
export async function getStudentList(
  skip: number,
  limit: number,
) {
  return axios({
    method: 'GET',
    url: queryString.stringifyUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/teachers/list`,
      query: {
        skip,
        limit,
      },
    }),
  }).then((response: AxiosResponse<StudentItemObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get course students list.
 * @param {ID} courseID
 * @return {Promise<CourseObject | ErrorObject | undefined>}
 */
export async function getCourseStudentList(
  courseID: ID,
) {
  return axios({
    method: 'POST',
    url: queryString.stringifyUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/teachers/courses/students/select`,
    }),
    data: JSON.stringify({
      courseID: courseID,
    }),
  }).then((response: AxiosResponse<StudentItemObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get tags
 * @return {Promise<Tag[] | ErrorObject | undefined>}
 */
export async function getTags() {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/courses/tags`,
  }).then((response: AxiosResponse<Tag[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get folders
 * @return {Promise<Folder[] | ErrorObject | undefined>}
 */
export async function getFolders() {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/courses/folders`,
  }).then((response: AxiosResponse<Folder[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}
